<template>
  <div class="portal-form-container" :class="[portalType === 'lender' ? 'portal-lender' : 'portal-customer']">
    <!-- Form Header -->
    <header class="form-header">
      <div class="container mx-auto px-4">
        <div class="py-4 md:py-6 relative">
          <!-- Portal Icon -->
          <div class="text-center mx-auto mb-3">
            <template v-if="headerIconSrc">
              <img 
                :src="headerIconSrc" 
                alt="Form Icon" 
                class="mx-auto h-12 w-12 md:h-16 md:w-16" 
              />
            </template>
            <template v-else>
              <img
                v-if="title.toLowerCase().includes('payment')"
                src="@/assets/images/ui/icons/makeAPayment.svg"
                alt="Make a Payment"
                class="mx-auto h-12 w-12 md:h-16 md:w-16"
                loading="lazy"
              />
              <img
                v-else-if="title.toLowerCase().includes('claim')"
                src="@/assets/images/ui/icons/claims.svg"
                alt="File a Claim"
                class="mx-auto h-12 w-12 md:h-16 md:w-16"
                loading="lazy"
              />
              <Icon
                v-else
                :icon="portalType === 'lender' ? 'fluent:building-bank-20-filled' : 'heroicons:home-solid'"
                :class="portalType === 'lender' ? 'text-blue-600' : 'text-green-600'"
                class="mx-auto text-3xl md:text-5xl"
              />
            </template>
          </div>
          
          <h1 class="form-title text-center">{{ title }}</h1>
          
          <!-- Cancel Button -->
          <button 
            @click="goBack"
            class="absolute top-4 right-4 text-gray-500 hover:text-gray-700 flex items-center text-sm"
            title="Cancel and go back"
          >
            <Icon icon="mdi:close" class="mr-1" />
            Cancel
          </button>
        </div>
        
        <!-- Form Progress -->
        <div v-if="steps.length > 1" class="mb-4">
          <FormProgressSteps 
            :steps="steps.map(step => ({
              title: step.label || step.title || `Step ${step.number || step.index}`,
              description: step.description
            }))"
            :current-step="currentStep"
            :active-color="portalType === 'lender' ? 'blue' : 'green'"
            :completed-steps="completedSteps"
            :portal-type="portalType"
          />
        </div>
      </div>
    </header>
    
    <!-- Form Recovery Banner (if applicable) -->
    <div 
      v-if="hasIncompleteDraft && !hideDraftBanner && formattedDraftTime !== '' && !sessionActive" 
      class="recovery-banner"
      :class="portalType === 'lender' ? 'recovery-banner-lender' : 'recovery-banner-customer'"
    >
      <div class="container mx-auto px-4">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <Icon icon="mdi:history" class="text-xl" :class="portalType === 'lender' ? 'text-blue-400' : 'text-amber-400'" />
          </div>
          <div class="ml-3 flex-grow">
            <p class="text-sm md:text-base" :class="portalType === 'lender' ? 'text-blue-800' : 'text-amber-800'">
              We've saved your form progress from {{ formattedDraftTime || 'earlier' }}. Would you like to continue where you left off?
            </p>
          </div>
          <div class="ml-2 flex-shrink-0">
            <button 
              @click="restoreDraft" 
              class="font-medium text-sm md:text-base underline"
              :class="portalType === 'lender' ? 'text-blue-800 hover:text-blue-900' : 'text-amber-800 hover:text-amber-900'"
            >
              Resume
            </button>
            <button 
              @click="hideDraftBanner = true" 
              class="ml-4 text-sm md:text-base"
              :class="portalType === 'lender' ? 'text-blue-800 hover:text-blue-900' : 'text-amber-800 hover:text-amber-900'"
            >
              Start New
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Authorization Reminder for Lenders -->
    <div v-if="portalType === 'lender' && showAuthReminder" class="auth-reminder">
      <div class="container mx-auto px-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <Icon icon="heroicons:exclamation-triangle" class="h-5 w-5 text-yellow-400" />
          </div>
          <div class="ml-3">
            <p class="text-sm">
              <strong>Important:</strong> You must upload a borrower's authorization form to make changes on behalf of your customer.
            </p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Main Form Content -->
    <main class="form-content">
      <div class="container mx-auto px-4">
        <!-- Loading State -->
        <div v-if="loading" class="py-8">
          <FormSkeleton
            :fields-count="4"
            :show-steps="false"
          />
        </div>
        
        <!-- Error State -->
        <div v-else-if="formError" class="error-container">
          <div class="error-icon">
            <Icon icon="mdi:alert-circle" class="text-red-500 text-4xl" />
          </div>
          <h2 class="error-title">Something went wrong</h2>
          <p class="error-message">{{ formError }}</p>
          <button 
            @click="resetError" 
            class="error-retry-button"
            :class="portalType === 'lender' ? 'btn-lender' : 'btn-customer'"
          >
            Try Again
          </button>
        </div>
        
        <!-- Success State -->
        <div v-else-if="formSubmitted && (currentStep > steps.length || steps.length === 0)">
          <FormSuccessMessage
            :title="successTitle || 'Success!'"
            :message="successMessage || 'Your form has been submitted successfully.'"
            :reference-number="referenceNumber"
            :details="successDetails"
            :next-steps="nextSteps"
            :primary-action="primarySuccessAction"
            :secondary-action="secondarySuccessAction"
            :enable-email-receipt="enableEmailReceipt"
            :default-email="userEmail"
            @primary-action="handlePrimaryAction"
            @secondary-action="handleSecondaryAction"
            @email-sent="handleEmailSent"
          />
        </div>
        
        <!-- Form Content -->
        <div v-else>
          <transition 
            name="fade-slide" 
            mode="out-in"
          >
            <div :key="currentStep">
              <slot></slot>
            </div>
          </transition>
        </div>
      </div>
    </main>
    
    <!-- Form Controls (if not showing step-specific controls) -->
    <footer v-if="!hideDefaultControls && !loading && !formError && !formSubmitted" class="form-footer">
      <div class="container mx-auto px-4">
        <div class="flex-responsive-row justify-between py-4">
          <button
            v-if="currentStep > 1"
            @click="goToPreviousStep"
            class="btn-secondary mobile-enhanced-button"
          >
            <Icon icon="mdi:arrow-left" class="mr-1" />
            Back
          </button>
          <div v-else class="invisible sm:hidden">
            <button class="btn-secondary mobile-enhanced-button opacity-0">Back</button>
          </div>
          
          <button
            v-if="currentStep < steps.length"
            @click="goToNextStep"
            :disabled="!canProceed"
            :class="portalType === 'lender' ? 'btn-lender' : 'btn-customer'"
            class="mobile-enhanced-button"
          >
            Next
            <Icon icon="mdi:arrow-right" class="ml-1" />
          </button>
          <button
            v-else
            @click="submitForm"
            :disabled="!canProceed || submitting"
            :class="portalType === 'lender' ? 'btn-lender' : 'btn-customer'"
            class="mobile-enhanced-button"
          >
            <span v-if="submitting">
              <Icon icon="mdi:loading" class="animate-spin mr-1" />
              Submitting...
            </span>
            <span v-else class="inline-flex items-center">
              Submit
              <Icon icon="mdi:check" class="ml-2 w-5 h-5" />
            </span>
          </button>
        </div>
      </div>
    </footer>
    
    <!-- Save Draft Indicator -->
    <div 
      v-if="showSavingIndicator" 
      class="saving-indicator"
      :class="{
        'saved': savedIndicatorState === 'saved',
        'saving-indicator-lender': portalType === 'lender',
        'saving-indicator-customer': portalType === 'customer'
      }"
    >
      <Icon 
        :icon="savedIndicatorState === 'saving' ? 'mdi:loading' : 'mdi:check-circle'" 
        class="saving-icon"
        :class="{'animate-spin': savedIndicatorState === 'saving'}"
      />
      <span>{{ savedIndicatorState === 'saving' ? 'Saving...' : 'Saved' }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { Icon } from '@iconify/vue';
import { useStorage } from '@vueuse/core';
import FormProgressSteps from './FormProgressSteps.vue';
import FormSkeleton from './FormSkeleton.vue';
import FormSuccessMessage from './FormSuccessMessage.vue';

// Add debounce utility function
const debounce = (fn, delay) => {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
};

// Add these constant definitions at the top of the script
const SAVE_THROTTLE_TIME = 3000; // minimum 3 seconds between saves
const VALIDATION_THROTTLE_TIME = 500; // minimum 500ms between validation checks

// Add expiration to drafts (7 days)
const DRAFT_EXPIRATION_DAYS = 7;

const props = defineProps({
  portalType: {
    type: String,
    required: true,
    validator: (value) => ['lender', 'customer'].includes(value)
  },
  title: {
    type: String,
    required: true
  },
  headerIconSrc: {
    type: String,
    default: null
  },
  currentStep: {
    type: Number,
    default: 1
  },
  steps: {
    type: Array,
    default: () => []
  },
  showAuthReminder: {
    type: Boolean,
    default: true
  },
  formId: {
    type: String,
    default: () => `form-${Date.now()}`
  },
  formValid: {
    type: Boolean,
    default: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  formSubmitted: {
    type: Boolean,
    default: false
  },
  formData: {
    type: Object,
    default: () => ({})
  },
  hideDefaultControls: {
    type: Boolean,
    default: false
  },
  userEmail: {
    type: String,
    default: ''
  },
  
  // Success state props
  successTitle: {
    type: String,
    default: ''
  },
  successMessage: {
    type: String,
    default: ''
  },
  referenceNumber: {
    type: String,
    default: ''
  },
  successDetails: {
    type: Array,
    default: () => []
  },
  nextSteps: {
    type: Array,
    default: () => []
  },
  primarySuccessAction: {
    type: String,
    default: 'Return to Dashboard'
  },
  secondarySuccessAction: {
    type: String,
    default: ''
  },
  enableEmailReceipt: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits([
  'update:formData',
  'step-change',
  'form-submit',
  'draft-restored',
  'back',
  'primary-action',
  'secondary-action',
  'email-sent'
]);

// Internal state
const formSubmitted = ref(false);
const formError = ref('');
const completedSteps = ref([]);
const submitting = ref(false);
const hasIncompleteDraft = ref(false);
const draftTimestamp = ref(null);
const hideDraftBanner = ref(false);
const savedIndicatorState = ref(''); // '', 'saving', 'saved'
const showSavingIndicator = ref(false);
const saveTimeout = ref(null);
const lastSaveTime = ref(0); // Add timestamp tracking
const saveDebounceTime = 2000; // Add debounce time in ms
const validationInProgress = ref(false); // Track if validation is in progress
const lastValidationTime = ref(0); // Last time validation ran
const sessionActive = ref(true); // Flag to track if this is an active session (vs returning to the form)

// Initialize form storage
const storageKey = computed(() => `form-draft-${props.portalType}-${props.formId}`);

// Add a cleanup function to clear corrupted localStorage entries
const cleanupLocalStorage = () => {
  try {
    console.log('[PortalForm] Cleaning up potentially corrupted localStorage entries');
    // Get all form-related keys
    const formKeys = Object.keys(localStorage).filter(key => key.includes('form-draft'));
    formKeys.forEach(key => {
      try {
        const value = localStorage.getItem(key);
        // Check if it's the problematic [object Object] string literal
        if (value === '[object Object]') {
          console.log(`[PortalForm] Removing corrupted entry for key: ${key}`);
          localStorage.removeItem(key);
        } else {
          // Try to parse it as JSON to see if it's valid
          try {
            JSON.parse(value);
          } catch (e) {
            console.log(`[PortalForm] Removing invalid JSON entry for key: ${key}`);
            localStorage.removeItem(key);
          }
        }
      } catch (keyError) {
        console.error(`[PortalForm] Error processing localStorage key ${key}:`, keyError);
      }
    });
  } catch (e) {
    console.error('[PortalForm] Error cleaning up localStorage:', e);
  }
};

// Clean up localStorage before initializing
cleanupLocalStorage();

const formStorage = useStorage(
  storageKey.value, 
  null,
  localStorage, // Explicitly specify localStorage as the storage adapter
  {
    serializer: {
      read: (v) => {
        try {
          if (!v) return null;
          
          // Special case for the literal string "[object Object]" which is invalid JSON
          if (v === '[object Object]') {
            console.log('[PortalForm] Found literal string "[object Object]" in storage, returning null');
            return null;
          }
          
          // Try to parse the value if it's a string
          const parsed = typeof v === 'string' ? JSON.parse(v) : v;
          console.log('[PortalForm] Successfully read and parsed storage value:', parsed);
          return parsed;
        } catch (e) {
          console.error('[PortalForm] Error reading storage:', e);
          // Remove the problematic key to prevent future errors
          try {
            localStorage.removeItem(storageKey.value);
            console.log(`[PortalForm] Removed problematic storage key: ${storageKey.value}`);
          } catch (removeError) {
            console.error('[PortalForm] Error removing problematic storage key:', removeError);
          }
          return null;
        }
      },
      write: (v) => {
        try {
          // Always serialize to JSON string for consistent storage
          const serialized = JSON.stringify(v);
          console.log('[PortalForm] Successfully serialized value for storage');
          return serialized;
        } catch (e) {
          console.error('[PortalForm] Error serializing for storage:', e);
          return JSON.stringify(null);
        }
      },
    },
  }
);

// Check for existing draft
onMounted(() => {
  try {
    console.log(`[PortalForm][DEBUG] Checking for saved draft with key: ${storageKey.value}`);
    console.log(`[PortalForm][DEBUG] Raw formStorage.value:`, formStorage.value);
    console.log(`[PortalForm][DEBUG] formStorage.value type: ${typeof formStorage.value}`);
    console.log(`[PortalForm][DEBUG] localStorage keys:`, Object.keys(localStorage).filter(key => key.includes('form-draft')));
    
    // By default, this is a new active session
    sessionActive.value = true;
    
    // Check for expired drafts
    if (formStorage.value && formStorage.value.timestamp) {
      const now = Date.now();
      const expirationTime = DRAFT_EXPIRATION_DAYS * 24 * 60 * 60 * 1000; // 7 days in ms
      const draftAge = now - formStorage.value.timestamp;
      
      console.log(`[PortalForm][DEBUG] Checking draft expiration:`);
      console.log(`[PortalForm][DEBUG] - Draft timestamp: ${new Date(formStorage.value.timestamp).toISOString()}`);
      console.log(`[PortalForm][DEBUG] - Current time: ${new Date(now).toISOString()}`);
      console.log(`[PortalForm][DEBUG] - Draft age: ${draftAge}ms (${Math.round(draftAge / (1000 * 60 * 60 * 24))} days)`);
      console.log(`[PortalForm][DEBUG] - Expiration threshold: ${expirationTime}ms (${DRAFT_EXPIRATION_DAYS} days)`);
      console.log(`[PortalForm][DEBUG] - Is expired: ${draftAge > expirationTime}`);
      
      if (draftAge > expirationTime) {
        console.log('[PortalForm][DEBUG] Draft expired, clearing storage');
        formStorage.value = null;
        hasIncompleteDraft.value = false;
        draftTimestamp.value = null;
      } 
      // Check if there's at least 5 seconds between the last save and now
      // This means the user likely left and came back rather than just navigating between steps
      else if (draftAge > 5000) {
        console.log('[PortalForm][DEBUG] Draft is from a previous session (age > 5 seconds)');
        sessionActive.value = false;
      }
    }
    
    // Initialize a valid draft object
    let validDraftData = null;
    
    if (formStorage.value) {
      // Analyze what we're working with
      let rawData = formStorage.value;
      const dataType = typeof rawData;
      const stringRepresentation = String(rawData);
      
      console.log('[PortalForm] Debug raw draft data:', {
        value: rawData,
        type: dataType,
        stringValue: stringRepresentation,
        objectToString: Object.prototype.toString.call(rawData),
        hasOwnProperty: rawData && (typeof rawData === 'object') ? Object.prototype.hasOwnProperty.call(rawData, 'timestamp') : false,
        keys: rawData && (typeof rawData === 'object') ? Object.keys(rawData) : []
      });
      
      // Case 1: It's already a valid object
      if (dataType === 'object' && rawData !== null) {
        console.log('[PortalForm] Draft is already an object, using directly');
        validDraftData = rawData;
      }
      // Case 2: It's a JSON string
      else if (dataType === 'string' && 
               stringRepresentation !== '[object Object]' && 
               (stringRepresentation.startsWith('{') || stringRepresentation.startsWith('['))) {
        console.log('[PortalForm] Draft appears to be a JSON string, attempting to parse');
        try {
          validDraftData = JSON.parse(stringRepresentation);
          console.log('[PortalForm] Successfully parsed JSON string into object:', validDraftData);
        } catch (parseError) {
          console.error('[PortalForm] Failed to parse JSON string:', parseError);
        }
      }
      // Case 3: It's the string "[object Object]" - try to recreate a minimal valid object
      else if (dataType === 'string' && stringRepresentation === '[object Object]') {
        console.log('[PortalForm] Draft is the string "[object Object]", attempting to create a valid object');
        validDraftData = {
          timestamp: Date.now(),
          formData: {},
          currentStep: 1,
          completedSteps: []
        };
        console.log('[PortalForm] Created default object for "[object Object]" case:', validDraftData);
        
        // Don't show the banner for empty default drafts
        hasIncompleteDraft.value = false;
        console.log('[PortalForm] Setting hasIncompleteDraft to false for empty default draft');
      }
      // Case 4: It's another primitive value
      else {
        console.warn(`[PortalForm] Draft is a ${dataType}, cannot use:`, rawData);
      }
      
      // If we have a valid draft object, process it
      if (validDraftData && typeof validDraftData === 'object') {
        console.log('[PortalForm] Working with valid draft object:', validDraftData);
        console.log('[PortalForm] Draft keys:', Object.keys(validDraftData));
        
        // Validate required fields
        if (!validDraftData.timestamp) {
          console.warn('[PortalForm] Draft missing timestamp, adding current time');
          validDraftData.timestamp = Date.now();
        }
        
        // Validate timestamp is a number/date
        const timestamp = validDraftData.timestamp;
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
          console.warn(`[PortalForm] Invalid draft timestamp: ${timestamp}, fixing with current time`);
          validDraftData.timestamp = Date.now();
        }
        
        // Set values after validation
        hasIncompleteDraft.value = true;
        draftTimestamp.value = validDraftData.timestamp;
        console.log(`[PortalForm] Draft timestamp set to: ${draftTimestamp.value}`);
        
        // Handle completedSteps (create if missing)
        if (!validDraftData.completedSteps) {
          console.warn('[PortalForm] Draft missing completedSteps, initializing empty array');
          validDraftData.completedSteps = [];
        } else if (!Array.isArray(validDraftData.completedSteps)) {
          console.warn('[PortalForm] Draft missing completedSteps, initializing empty array');
          validDraftData.completedSteps = [];
        }
        
        completedSteps.value = validDraftData.completedSteps;
        console.log(`[PortalForm] Restored ${completedSteps.value.length} completed steps`);
        
        // Save the validated object back to storage if needed
        if (validDraftData !== formStorage.value) {
          console.log('[PortalForm] Updating storage with validated draft object');
          formStorage.value = validDraftData;
        }
      } else {
        console.log('[PortalForm] No valid draft object could be created or parsed');
      }
    } else {
      console.log('[PortalForm] No saved draft found');
    }
    
    // Create a debounced version of the save function with increased protection
    const debouncedSave = debounce(() => {
      const now = Date.now();
      // Skip saves that happen too frequently
      if (now - lastSaveTime.value < SAVE_THROTTLE_TIME) {
        console.log(`[PortalForm] Skipping save, too frequent (${now - lastSaveTime.value}ms since last save)`);
        return;
      }
      
      if (props.formData && typeof props.formData === 'object') {
        saveDraft();
      }
    }, saveDebounceTime);
    
    // Auto-save form data when it changes
    watch(() => props.formData, (newValue) => {
      if (newValue && typeof newValue === 'object') {
        debouncedSave();
      }
    }, { deep: true });
    
    // Also debounce the watch on formValid to prevent infinite loop
    watch(() => props.formValid, (newValue) => {
      // Don't trigger saves on formValid changes
      console.log(`[PortalForm] formValid changed to ${newValue}, but not triggering save`);
    });
    
  } catch (error) {
    console.error('[PortalForm] Error loading draft:', error);
    // Reset the draft state to prevent issues
    hasIncompleteDraft.value = false;
    draftTimestamp.value = null;
  }
});

// Clean up any timeouts
onBeforeUnmount(() => {
  if (saveTimeout.value) {
    clearTimeout(saveTimeout.value);
  }
});

// Methods
const goBack = () => {
  emit('back');
};

const goToNextStep = () => {
  console.log("[PortalForm] goToNextStep method **ENTERED**."); // Explicit entry log
  console.log(`[PortalForm] Inside goToNextStep - localCurrentStep: ${props.currentStep}, steps.length: ${props.steps.length}, props.formValid: ${props.formValid}`);
  
  if (props.currentStep < props.steps.length && props.formValid) {
    // Mark current step as completed
    if (completedSteps.value && Array.isArray(completedSteps.value) && !completedSteps.value.includes(props.currentStep - 1)) {
      completedSteps.value.push(props.currentStep - 1);
    }
    
    // Move to next step
    emit('step-change', props.currentStep + 1);
    
    // Save draft with completed steps
    saveDraft();
    
    // Scroll to top of form
    scrollToTop();
  }
};

const goToPreviousStep = () => {
  if (props.currentStep > 1) {
    emit('step-change', props.currentStep - 1);
    scrollToTop();
  }
};

const scrollToTop = () => {
  // Scroll to the top of the form container with smooth animation
  const formContainer = document.querySelector('.portal-form-container');
  if (formContainer) {
    formContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

// Function to check if formData has actual meaningful values
const hasActualFormData = (formData) => {
  console.log('[PortalForm][DEBUG] Running hasActualFormData check');
  // If not an object or null/undefined, there's no data
  if (!formData || typeof formData !== 'object') {
    console.log('[PortalForm][DEBUG] FormData is not an object or is null/undefined:', formData);
    return false;
  }
  
  // Check if there are any properties
  const keys = Object.keys(formData);
  console.log(`[PortalForm][DEBUG] FormData has ${keys.length} keys:`, keys);
  
  if (keys.length === 0) {
    console.log('[PortalForm][DEBUG] FormData has no keys, returning false');
    return false;
  }
  
  // Check if any of the properties have actual values
  const hasValue = keys.some(key => {
    const value = formData[key];
    console.log(`[PortalForm][DEBUG] Checking key "${key}" with value:`, value);
    
    // Skip null/undefined values
    if (value === null || value === undefined) {
      console.log(`[PortalForm][DEBUG] Key "${key}" has null/undefined value`);
      return false;
    }
    
    // Handle string values - check if they're not just whitespace
    if (typeof value === 'string') {
      const isNotEmpty = value.trim() !== '';
      console.log(`[PortalForm][DEBUG] Key "${key}" is string ${isNotEmpty ? 'with' : 'without'} content: "${value}"`);
      return isNotEmpty;
    }
    
    // Handle array values - check if they have elements
    if (Array.isArray(value)) {
      const hasElements = value.length > 0;
      console.log(`[PortalForm][DEBUG] Key "${key}" is array with ${value.length} elements`);
      return hasElements;
    }
    
    // Handle object values - check if they have properties (recursive check)
    if (typeof value === 'object') {
      console.log(`[PortalForm][DEBUG] Key "${key}" is object, checking recursively`);
      return hasActualFormData(value);
    }
    
    // Any other type (number, boolean) is considered a value
    console.log(`[PortalForm][DEBUG] Key "${key}" has value of type ${typeof value}: ${value}`);
    return true;
  });
  
  console.log(`[PortalForm][DEBUG] Final hasActualFormData result: ${hasValue}`);
  return hasValue;
};

const saveDraft = () => {
  try {
    console.log('[PortalForm][DEBUG] Starting saveDraft function');
    // Hard limit on save frequency
    const now = Date.now();
    if (now - lastSaveTime.value < SAVE_THROTTLE_TIME) {
      console.log(`[PortalForm][DEBUG] Skipping save, last save was ${now - lastSaveTime.value}ms ago (throttle: ${SAVE_THROTTLE_TIME}ms)`);
      return;
    }
    
    lastSaveTime.value = now;
    
    // Show saving indicator
    savedIndicatorState.value = 'saving';
    showSavingIndicator.value = true;
    
    // Log detailed information about what we're saving
    console.log('[PortalForm][DEBUG] Starting draft save operation');
    console.log('[PortalForm][DEBUG] Current formData keys:', props.formData ? Object.keys(props.formData) : 'none');
    console.log('[PortalForm][DEBUG] formData type:', typeof props.formData);
    console.log('[PortalForm][DEBUG] Current step:', props.currentStep);
    
    // Validate required fields before saving
    if (!props.formData || typeof props.formData !== 'object') {
      console.error('[PortalForm][DEBUG] Cannot save draft: formData is missing or not an object');
      savedIndicatorState.value = '';
      showSavingIndicator.value = false;
      return;
    }
    
    // Only save if there's actual data worth saving
    console.log('[PortalForm][DEBUG] Checking if formData has meaningful values');
    const hasMeaningfulData = hasActualFormData(props.formData);
    console.log(`[PortalForm][DEBUG] FormData has meaningful values: ${hasMeaningfulData}`);
    
    if (!hasMeaningfulData) {
      console.log('[PortalForm][DEBUG] Skipping save: No meaningful data to save');
      savedIndicatorState.value = '';
      showSavingIndicator.value = false;
      return;
    }
    
    // Ensure completedSteps is a valid array
    const safeCompletedSteps = Array.isArray(completedSteps.value) ? completedSteps.value : [];
    
    // Create timestamp and validate it
    const timestamp = Date.now();
    console.log(`[PortalForm] Saving draft with timestamp: ${timestamp}`);
    
    // Create the draft object
    const draftObject = {
      formData: props.formData,
      currentStep: props.currentStep,
      completedSteps: safeCompletedSteps,
      timestamp: timestamp
    };
    
    console.log('[PortalForm] Draft object to save:', draftObject);
    console.log('[PortalForm] Draft object type:', Object.prototype.toString.call(draftObject));
    
    // Always use a fresh, validated object for storage
    try {
      // Double-check we're saving a valid object by serializing and parsing it
      const serialized = JSON.stringify(draftObject);
      const validated = JSON.parse(serialized);
      
      console.log('[PortalForm] Validated draft object through JSON serialization');
      console.log('[PortalForm] Final object to save to storage:', validated);
      
      // Save to storage
      formStorage.value = validated;
      
      // Verify what was saved
      console.log('[PortalForm] Storage after save:', formStorage.value);
      console.log('[PortalForm] Storage type after save:', typeof formStorage.value);
      
      // Show "Saved" message briefly
      if (saveTimeout.value) {
        clearTimeout(saveTimeout.value);
      }
      
      // Update to "Saved" after a short delay to show the saving animation
      setTimeout(() => {
        savedIndicatorState.value = 'saved';
        
        // Hide the indicator after a few seconds
        saveTimeout.value = setTimeout(() => {
          showSavingIndicator.value = false;
        }, 2000);
      }, 800);
      
      // Set draft flag for the banner
      hasIncompleteDraft.value = true;
      draftTimestamp.value = timestamp;
      
      // Since we're actively saving, this is considered an active session
      sessionActive.value = true;
      
      console.log('[PortalForm] Draft save operation completed successfully');
    } catch (serializationError) {
      console.error('[PortalForm] Failed to serialize draft object:', serializationError);
      
      // Fallback: try to save the original object directly
      console.log('[PortalForm] Attempting to save original object directly');
      formStorage.value = draftObject;
      
      // Simplified success message
      savedIndicatorState.value = 'saved';
      setTimeout(() => {
        showSavingIndicator.value = false;
      }, 2000);
      
      hasIncompleteDraft.value = true;
      draftTimestamp.value = timestamp;
      
      // Since we're actively saving, this is considered an active session
      sessionActive.value = true;
    }
  } catch (error) {
    console.error('[PortalForm] Error saving draft:', error);
    console.error('[PortalForm] Error details:', {
      message: error.message,
      stack: error.stack,
      formData: props.formData ? 'exists' : 'missing',
      formDataType: typeof props.formData,
      completedSteps: completedSteps.value ? 'exists' : 'missing'
    });
    
    savedIndicatorState.value = '';
    showSavingIndicator.value = false;
  }
};

const restoreDraft = () => {
  try {
    console.log('[PortalForm][DEBUG] Starting restoreDraft function');
    
    if (!formStorage.value) {
      console.error('[PortalForm][DEBUG] Cannot restore draft: No draft data available');
      return;
    }
    
    console.log('[PortalForm][DEBUG] Attempting to restore draft from storage');
    console.log('[PortalForm][DEBUG] Raw formStorage.value for restore:', formStorage.value);
    console.log('[PortalForm][DEBUG] Storage type:', typeof formStorage.value);
    
    // Initialize a valid draft object
    let validDraftData = null;
    
    // Analyze what we're working with
    let rawData = formStorage.value;
    const dataType = typeof rawData;
    const stringRepresentation = String(rawData);
    
    console.log('[PortalForm][DEBUG] Debug raw restore data:', {
      value: rawData,
      type: dataType,
      stringValue: stringRepresentation,
      objectToString: Object.prototype.toString.call(rawData),
      hasOwnProperty: rawData && (typeof rawData === 'object') ? Object.prototype.hasOwnProperty.call(rawData, 'formData') : false,
      keys: rawData && (typeof rawData === 'object') ? Object.keys(rawData) : []
    });
    
    // Case 1: It's already a valid object
    if (dataType === 'object' && rawData !== null) {
      console.log('[PortalForm][DEBUG] Restore data is already an object, using directly');
      validDraftData = rawData;
    }
    // Case 2: It's a JSON string
    else if (dataType === 'string' && 
             stringRepresentation !== '[object Object]' && 
             (stringRepresentation.startsWith('{') || stringRepresentation.startsWith('['))) {
      console.log('[PortalForm][DEBUG] Restore data appears to be a JSON string, attempting to parse');
      try {
        validDraftData = JSON.parse(stringRepresentation);
        console.log('[PortalForm][DEBUG] Successfully parsed JSON string for restore:', validDraftData);
      } catch (parseError) {
        console.error('[PortalForm][DEBUG] Failed to parse JSON string for restore:', parseError);
        return;
      }
    }
    // Case 3: It's the string "[object Object]" - try to recreate a minimal valid object
    else if (dataType === 'string' && stringRepresentation === '[object Object]') {
      console.log('[PortalForm][DEBUG] Restore data is the string "[object Object]", cannot proceed with restore');
      return;
    }
    // Case 4: It's another primitive value
    else {
      console.warn(`[PortalForm][DEBUG] Restore data is a ${dataType}, cannot use:`, rawData);
      return;
    }
    
    // Validate the draft data
    if (!validDraftData || typeof validDraftData !== 'object') {
      console.error('[PortalForm][DEBUG] Invalid restore data - not an object:', validDraftData);
      return;
    }
    
    // Validate the form data is present
    if (!validDraftData.formData) {
      console.error('[PortalForm][DEBUG] Restore data missing formData');
      return;
    }
    
    // Check if form data is empty - no need to restore empty data
    const isEmptyFormData = typeof validDraftData.formData !== 'object' || 
                           Object.keys(validDraftData.formData).length === 0;
    
    console.log('[PortalForm][DEBUG] Checking if form data is empty:', isEmptyFormData);
    console.log('[PortalForm][DEBUG] formData type:', typeof validDraftData.formData);
    console.log('[PortalForm][DEBUG] formData keys:', validDraftData.formData ? Object.keys(validDraftData.formData) : 'none');
    
    if (isEmptyFormData) {
      console.warn('[PortalForm][DEBUG] Form data is empty, nothing to restore');
      hideDraftBanner.value = true;
      return;
    }
    
    // Check for meaningful data
    console.log('[PortalForm][DEBUG] Checking if form data has meaningful values');
    const hasMeaningfulData = hasActualFormData(validDraftData.formData);
    console.log(`[PortalForm][DEBUG] Form data has meaningful values: ${hasMeaningfulData}`);
    
    if (!hasMeaningfulData) {
      console.warn('[PortalForm][DEBUG] Form data has no meaningful values, nothing to restore');
      hideDraftBanner.value = true;
      return;
    }
    
    // Restore form data
    console.log('[PortalForm][DEBUG] Emitting update:formData with validated draft data');
    emit('update:formData', validDraftData.formData);
    console.log('[PortalForm][DEBUG] Form data restored');
    
    // Restore current step with validation
    const targetStep = validDraftData.currentStep || 1;
    console.log(`[PortalForm] Restoring to step ${targetStep}`);
    
    if (targetStep < 1 || targetStep > props.steps.length) {
      console.warn(`[PortalForm] Invalid step number in restore data (${targetStep}), defaulting to step 1`);
      emit('step-change', 1);
    } else {
      emit('step-change', targetStep);
    }
    
    // Restore completed steps with validation
    if (!validDraftData.completedSteps) {
      console.warn('[PortalForm] Restore data missing completedSteps, initializing empty array');
      validDraftData.completedSteps = [];
    } else if (!Array.isArray(validDraftData.completedSteps)) {
      console.warn('[PortalForm] Restore data completedSteps is not an array, initializing empty array');
      validDraftData.completedSteps = [];
    }
    
    completedSteps.value = validDraftData.completedSteps;
    console.log(`[PortalForm] Restored ${completedSteps.value.length} completed steps`);
    
    // Update storage with valid object if needed
    if (validDraftData !== formStorage.value) {
      console.log('[PortalForm] Updating storage with validated restore object');
      formStorage.value = validDraftData;
    }
    
    // Hide the banner
    hideDraftBanner.value = true;
    
    // Once draft is restored, this becomes an active session
    sessionActive.value = true;
    
    // Emit event
    emit('draft-restored', validDraftData);
    console.log('[PortalForm] Draft restoration complete');
  } catch (error) {
    console.error('[PortalForm] Error restoring draft:', error);
    // Display an error message to the user if needed
  }
};

const submitForm = async () => {
  if (!canProceed.value || submitting.value) return;
  
  try {
    submitting.value = true;
    console.log('[PortalForm][DEBUG] Starting form submission process');
    
    // Mark all steps as completed
    for (let i = 0; i < props.steps.length; i++) {
      if (!completedSteps.value.includes(i)) {
        completedSteps.value.push(i);
      }
    }
    
    // Emit submit event and wait for parent to handle it
    console.log('[PortalForm][DEBUG] Emitting form-submit event with data', props.formData);
    await emit('form-submit', props.formData);
    console.log('[PortalForm][DEBUG] Form submission completed by parent component');
    
    // Success state
    formSubmitted.value = true;
    console.log('[PortalForm][DEBUG] formSubmitted set to true');
    
    // Clear form draft from storage
    console.log('[PortalForm][DEBUG] Storage key being cleared:', storageKey.value);
    console.log('[PortalForm][DEBUG] Previous storage value:', formStorage.value);
    formStorage.value = null;
    hasIncompleteDraft.value = false;
    draftTimestamp.value = null;
    console.log('[PortalForm][DEBUG] Draft data cleared after successful submission');
    console.log('[PortalForm][DEBUG] Verifying storage is clear:', formStorage.value);
  } catch (error) {
    console.error('[PortalForm][DEBUG] Form submission error:', error);
    formError.value = error.message || 'There was an error submitting your form. Please try again.';
  } finally {
    submitting.value = false;
  }
};

// Special watcher to detect when currentStep is set to success step
watch(() => props.currentStep, (newValue) => {
  if (newValue > props.steps.length) {
    console.log(`[PortalForm] currentStep (${newValue}) exceeds steps length (${props.steps.length}), setting formSubmitted to true`);
    formSubmitted.value = true;
  }
});

const resetError = () => {
  formError.value = '';
};

const handlePrimaryAction = () => {
  emit('primary-action');
};

const handleSecondaryAction = () => {
  emit('secondary-action');
};

const handleEmailSent = (email) => {
  emit('email-sent', email);
};

// Format timestamp to readable date/time
const formatTimestamp = (timestamp) => {
  try {
    if (!timestamp) {
      console.warn('[PortalForm] Missing timestamp for formatting');
      return '';
    }
    
    console.log(`[PortalForm] Formatting timestamp: ${timestamp}`);
    
    // Validate the timestamp is a valid date
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      console.error(`[PortalForm] Invalid timestamp value: ${timestamp}`);
      return '';
    }
    
    const now = new Date();
    const diffMs = now - date;
    
    // Ensure diffMs is a valid number
    if (isNaN(diffMs)) {
      console.error(`[PortalForm] Invalid date difference calculation: ${now} - ${date}`);
      return '';
    }
    
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMins < 1) {
      return 'just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    } else if (diffMins < 1440) {
      const hours = Math.floor(diffMins / 60);
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    } else {
      // Format as date for older drafts
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  } catch (error) {
    console.error('[PortalForm] Error formatting timestamp:', error);
    return '';
  }
};

// Compute formatted draft time once to avoid multiple calculations
const formattedDraftTime = computed(() => {
  console.log('[PortalForm][DEBUG] Computing formattedDraftTime');
  console.log('[PortalForm][DEBUG] hasIncompleteDraft:', hasIncompleteDraft.value);
  console.log('[PortalForm][DEBUG] draftTimestamp:', draftTimestamp.value);
  
  if (!hasIncompleteDraft.value || !draftTimestamp.value) {
    console.log('[PortalForm][DEBUG] Missing hasIncompleteDraft or draftTimestamp, returning empty string');
    return '';
  }
  
  // Make sure we have form storage data
  if (!formStorage.value) {
    console.log('[PortalForm][DEBUG] No formStorage data available');
    return '';
  }
  
  console.log('[PortalForm][DEBUG] FormStorage data:', formStorage.value);
  
  // Check for timestamp and enforce expiration
  if (formStorage.value.timestamp) {
    const now = Date.now();
    const expirationTime = DRAFT_EXPIRATION_DAYS * 24 * 60 * 60 * 1000; // 7 days in ms
    const age = now - formStorage.value.timestamp;
    const maxAge = expirationTime;
    
    console.log('[PortalForm][DEBUG] Draft age check:');
    console.log(`[PortalForm][DEBUG] - Current time: ${new Date(now).toISOString()}`);
    console.log(`[PortalForm][DEBUG] - Draft timestamp: ${new Date(formStorage.value.timestamp).toISOString()}`);
    console.log(`[PortalForm][DEBUG] - Age: ${age}ms (${Math.round(age / (1000 * 60 * 60 * 24))} days)`);
    console.log(`[PortalForm][DEBUG] - Max age: ${maxAge}ms (${DRAFT_EXPIRATION_DAYS} days)`);
    console.log(`[PortalForm][DEBUG] - Expired: ${age > maxAge}`);
    
    if (age > maxAge) {
      console.log('[PortalForm][DEBUG] Draft expired in formattedDraftTime, not showing banner');
      return '';
    }
  }
  
  // Don't show formatted time for empty drafts
  const isEmpty = !formStorage.value?.formData || 
    typeof formStorage.value?.formData !== 'object' || 
    Object.keys(formStorage.value?.formData).length === 0;
  
  console.log('[PortalForm][DEBUG] Checking if draft is empty:', isEmpty);
  console.log('[PortalForm][DEBUG] formData type:', typeof formStorage.value?.formData);
  console.log('[PortalForm][DEBUG] formData keys:', formStorage.value?.formData ? Object.keys(formStorage.value.formData) : 'none');
  
  if (isEmpty) {
    console.log('[PortalForm][DEBUG] Draft formData is empty, not showing timestamp');
    return '';
  }
  
  // Check for meaningful data using our validation function
  const hasMeaningfulData = hasActualFormData(formStorage.value.formData);
  console.log('[PortalForm][DEBUG] Draft has meaningful data:', hasMeaningfulData);
  
  if (!hasMeaningfulData) {
    console.log('[PortalForm][DEBUG] Draft formData has no meaningful values, not showing timestamp');
    return '';
  }
  
  const formattedTime = formatTimestamp(draftTimestamp.value);
  console.log(`[PortalForm][DEBUG] Final formatted time: "${formattedTime}"`);
  return formattedTime;
});

// Computed properties
const canProceed = computed(() => {
  // Throttle the validation checks to prevent excessive computation
  const now = Date.now();
  if (validationInProgress.value || (now - lastValidationTime.value < VALIDATION_THROTTLE_TIME)) {
    // Return the last known result to avoid triggering reactivity
    return false; 
  }
  
  validationInProgress.value = true;
  lastValidationTime.value = now;
  
  try {
    const lastStepReached = props.currentStep === props.steps.length;
    console.log(`[PortalForm] canProceed computed. localCurrentStep: ${props.currentStep}, props.formValid: ${props.formValid}, isLastStep: ${lastStepReached}, submitting: ${submitting.value}`);
    
    // Handle the case where props.formValid is a Promise
    if (props.formValid instanceof Promise) {
      console.warn('[PortalForm] formValid is a Promise, defaulting to false until resolved');
      return false;
    }
    
    // Ensure we're working with a boolean
    const formIsValid = typeof props.formValid === 'boolean' ? props.formValid : !!props.formValid;
    
    if (lastStepReached) {
      return formIsValid && !submitting.value;
    } else {
      return formIsValid && !submitting.value;
    }
  } finally {
    // Reset validation state after a short delay
    setTimeout(() => {
      validationInProgress.value = false;
    }, 100);
  }
});
</script>

<style scoped>
.portal-form-container {
  @apply min-h-screen bg-gray-50 flex flex-col;
}

.form-header {
  @apply bg-white border-b border-gray-200 py-2;
}

.form-title {
  @apply text-2xl md:text-3xl font-bold text-gray-800;
}

.recovery-banner {
  @apply py-2 md:py-3 border-y;
}

.recovery-banner-customer {
  @apply bg-amber-50 border-amber-200;
}

.recovery-banner-lender {
  @apply bg-blue-50 border-blue-200;
}

.auth-reminder {
  @apply py-2 md:py-3 bg-yellow-50 border-y border-yellow-200;
}

.form-content {
  @apply flex-grow py-6 md:py-8;
}

.form-footer {
  @apply bg-white border-t border-gray-200 sticky bottom-0 z-10;
}

.error-container {
  @apply text-center py-8 max-w-md mx-auto;
}

.error-icon {
  @apply mb-4;
}

.error-title {
  @apply text-xl font-bold text-gray-800 mb-2;
}

.error-message {
  @apply text-gray-600 mb-6;
}

/*
.btn-customer {
  @apply bg-green-600 hover:bg-green-700 text-white py-2 px-6 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05); // Subtle shadow
  @apply border-t border-green-500; // Just top border for subtle depth
}

.btn-lender {
  @apply bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05); // Subtle shadow
  @apply border-t border-blue-500; // Just top border for subtle depth
}
*/

.btn-secondary {
  @apply bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-6 rounded-md font-medium transition-colors flex items-center justify-center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05); /* Subtle shadow */
}

.flex-responsive-row {
  @apply flex flex-col sm:flex-row gap-3 sm:gap-0;
}

.mobile-enhanced-button {
  @apply py-3 px-4 text-base sm:py-2 sm:px-4 sm:text-sm w-full sm:w-auto;
}

.saving-indicator {
  @apply fixed bottom-4 right-4 shadow-md rounded-full px-3 py-2 text-sm flex items-center text-gray-600 border z-20;
}

.saving-indicator-customer {
  @apply bg-white border-gray-200;
}

.saving-indicator-customer.saved {
  @apply text-green-600 border-green-200;
}

.saving-indicator-lender {
  @apply bg-white border-gray-200;
}

.saving-indicator-lender.saved {
  @apply text-blue-600 border-blue-200;
}

.saving-icon {
  @apply mr-1;
}

/* Transitions */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* Step indicator styles (preserved from original) */
.portal-lender .step-indicator {
  @apply bg-blue-100 text-blue-800 border-blue-300;
}

.portal-lender .current-step {
  @apply bg-blue-500 text-white border-blue-700;
}

.portal-lender .active-step {
  @apply bg-blue-700 text-white border-blue-900;
}

.portal-customer .step-indicator {
  @apply bg-green-100 text-green-800 border-green-300;
}

.portal-customer .current-step {
  @apply bg-green-500 text-white border-green-700;
}

.portal-customer .active-step {
  @apply bg-green-700 text-white border-green-900;
}

.step-indicator {
  @apply flex items-center justify-center w-8 h-8 rounded-full;
  @apply border-2;
  @apply transition-all duration-300;
}
</style> 